import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import AdminLayout from '@baaligames/shared-ui/admin/components/AdminLayout';
import AvatarCropper from '@baaligames/shared-ui/components/AvatarCropper';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi, getAdminAccessToken } from '@baaligames/shared-ui/admin/lib/adminApi';
import { compressForUpload } from '@baaligames/shared-ui/lib/imageCompress';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { getGame } from '@baaligames/shared-ui/games/registry';

type Bot = {
  id: number;
  name: string;
  gender: 'male' | 'female';
  personality_type: 'easy' | 'medium' | 'hard';
  avatar_path: string | null;
  wallet_balance: number;
  is_active: 0 | 1;
  games_played: number;
  matches_won: number;
  total_spent: number;
  total_won: number;
  created_at: string;
  updated_at: string;
  // 2026-05-17 (Alternative Fee Policy follow-up): per-bot lifetime
  // platform-fee revenue (sum of admin_wallet_ledger.platform_fee_bot_share
  // where counterparty=this bot). Returned by adminBots.getOne — see
  // shared/src/core/controllers/adminBots.controller.js.
  platform_fees_collected?: number;
  platform_fee_entries?: number;
  platform_match_yield?: number;       // total_spent − total_won, platform's view
  platform_true_net_yield?: number;    // match yield + fees collected
};

type LedgerRow = {
  id: number;
  game: string;              // registered game id OR 'admin' for direct-admin movements
  direction: 'debit' | 'credit';
  amount: number;
  balance_after: number;
  reason: string;
  match_ref: number | null;
  note: string | null;
  actor_admin_id: number | null;
  created_at: string;
};

const REASON_LABEL: Record<string, { label: string; icon: string }> = {
  match_entry:  { label: 'Match entry',     icon: 'sports_esports' },
  match_prize:  { label: 'Match prize',     icon: 'emoji_events' },
  match_refund: { label: 'Match refund',    icon: 'undo' },
  admin_topup:  { label: 'Admin top-up',    icon: 'add_circle' },
  admin_adjust: { label: 'Admin adjust',    icon: 'tune' },
  seed:         { label: 'Initial seed',    icon: 'stars' },
};

// Resolve a game id on a ledger row into its admin-friendly label.
// Registered games contribute their `displayName`; the pseudo-id 'admin'
// (direct admin credits/debits) gets a fixed label; anything else falls
// back to the raw id so deprecated-but-historical game rows still render.
function gameLabel(id: string): string {
  if (id === 'admin') return 'Admin';
  return getGame(id)?.displayName || id;
}

export default function AdminBotDetail() {
  const { id } = useParams<{ id: string }>();
  const bid = Number(id);
  const nav = useNavigate();
  const [bot, setBot] = useState<Bot | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { bot: b } = await adminApi.get<{ bot: Bot }>(`/admin/bots/${bid}`);
      setBot(b);
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setLoading(false); }
  }, [bid]);

  useEffect(() => { load(); }, [load]);

  function note(msg: string) { setFlash(msg); setTimeout(() => setFlash(null), 3000); }

  return (
    <AdminLayout title={bot ? bot.name : 'Bot'} subtitle={bot ? `Bot #${bot.id}` : undefined} titleIcon="smart_toy">
      {loading && <div className="p-10 text-center text-arena-500 dark:text-neon-200/55"><Icon name="progress_activity" size={28} className="animate-spin inline-block" /> Loading…</div>}
      {err && <div className="p-4 rounded-xl bg-rose-500/10 ring-1 ring-inset ring-rose-500/30 text-red-700 dark:text-red-200 text-sm">{err}</div>}
      {flash && (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          className="mb-4 rounded-xl bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 text-emerald-700 dark:text-emerald-200 px-4 py-2 text-sm inline-flex items-center gap-2">
          <Icon name="check_circle" size={16} filled /> {flash}
        </motion.div>
      )}
      {!loading && bot && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <IdentityCard bot={bot} reload={load} note={note} onDeleted={() => nav('/bots', { replace: true })} />
          <div className="space-y-4 lg:col-span-1">
            <WalletCard bot={bot} reload={load} note={note} />
            <StatsCard bot={bot} />
          </div>
          <LedgerCard botId={bot.id} />
        </div>
      )}
    </AdminLayout>
  );
}

function IdentityCard({ bot, reload, note, onDeleted }: {
  bot: Bot; reload: () => void; note: (s: string) => void; onDeleted: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [cropping, setCropping] = useState(false);

  async function uploadAvatar(rawFile: File) {
    setUploading(true);
    try {
      const file = await compressForUpload(rawFile, { preset: 'avatar' });
      const fd = new FormData();
      fd.append('avatar', file);
      const t = getAdminAccessToken();
      const res = await fetch(`/api/admin/bots/${bot.id}/avatar`, {
        method: 'POST', credentials: 'include',
        headers: t ? { authorization: `Bearer ${t}` } : {},
        body: fd,
      });
      const j = await res.json();
      if (!j.ok) throw new Error(j.error?.message || 'Upload failed');
      note('Avatar updated');
      reload();
    } catch (e) {
      alert((e as Error).message);
      throw e;
    } finally { setUploading(false); }
  }

  async function togglePersonality(p: Bot['personality_type']) {
    try {
      await adminApi.patch(`/admin/bots/${bot.id}`, { personality_type: p });
      note(`Personality set to ${p}`);
      reload();
    } catch (e) { alert((e as ApiError).message); }
  }
  async function toggleGender(g: Bot['gender']) {
    try {
      await adminApi.patch(`/admin/bots/${bot.id}`, { gender: g });
      note(`Gender set to ${g}`);
      reload();
    } catch (e) { alert((e as ApiError).message); }
  }
  async function toggleActive() {
    try {
      await adminApi.patch(`/admin/bots/${bot.id}`, { is_active: !bot.is_active });
      note(bot.is_active ? 'Bot deactivated' : 'Bot activated');
      reload();
    } catch (e) { alert((e as ApiError).message); }
  }

  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(bot.name);
  useEffect(() => { setNewName(bot.name); }, [bot.name]);
  async function saveName() {
    if (!newName.trim() || newName === bot.name) { setRenaming(false); return; }
    try {
      await adminApi.patch(`/admin/bots/${bot.id}`, { name: newName.trim() });
      note('Name updated');
      setRenaming(false);
      reload();
    } catch (e) { alert((e as ApiError).message); }
  }

  const [confirmDel, setConfirmDel] = useState('');
  async function doDelete() {
    if (confirmDel !== bot.name) return;
    try {
      await adminApi.del(`/admin/bots/${bot.id}`);
      note('Bot deleted');
      onDeleted();
    } catch (e) { alert((e as ApiError).message); }
  }

  return (
    <div className="rounded-2xl bg-white dark:bg-arena-800/60 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-5 lg:col-span-1">
      <div className="flex flex-col items-center">
        <div className="relative">
          <div className="w-32 h-32 rounded-2xl bg-arena-100/60 dark:bg-arena-800 grid place-items-center overflow-hidden shadow-lg">
            {bot.avatar_path
              ? <img src={bot.avatar_path} alt="" className="w-full h-full object-cover" />
              : <Icon name={bot.gender === 'female' ? 'face_3' : 'face_6'} size={64} className="text-arena-600 dark:text-neon-200/60" />}
          </div>
          <button onClick={() => setCropping(true)} disabled={uploading}
            className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-violet-600 hover:bg-violet-500 text-white grid place-items-center shadow-lg transition">
            {uploading ? <Icon name="progress_activity" size={18} className="animate-spin" /> : <Icon name="photo_camera" size={18} />}
          </button>
        </div>
        {cropping && (
          <AvatarCropper
            title={`Adjust photo for ${bot.name}`}
            onUpload={uploadAvatar}
            onClose={() => setCropping(false)}
          />
        )}

        <div className="mt-3 text-center">
          {renaming ? (
            <div className="flex items-center gap-1">
              <input value={newName} onChange={(e) => setNewName(e.target.value)}
                className="rounded-lg bg-arena-100/60 dark:bg-arena-800 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-2 py-1 text-sm text-center font-bold focus:outline-none focus:ring-2 focus:ring-violet-400" />
              <button onClick={saveName} className="p-1 rounded hover:bg-arena-100/60 dark:hover:bg-arena-700/60"><Icon name="check" size={16} className="text-emerald-400" /></button>
              <button onClick={() => { setRenaming(false); setNewName(bot.name); }} className="p-1 rounded hover:bg-arena-100/60 dark:hover:bg-arena-700/60"><Icon name="close" size={16} className="text-red-400" /></button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{bot.name}</h2>
              <button onClick={() => setRenaming(true)} className="p-1 rounded hover:bg-arena-100/60 dark:hover:bg-arena-700/60"><Icon name="edit" size={14} className="text-arena-600 dark:text-neon-200/60" /></button>
            </div>
          )}
          <div className="text-xs text-arena-600 dark:text-neon-200/60">Bot #{bot.id}</div>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <label className="block text-[10px] uppercase text-arena-600 dark:text-neon-200/60 font-bold mb-1">Gender</label>
          <div className="flex gap-2">
            {(['female', 'male'] as const).map((g) => (
              <button key={g} onClick={() => toggleGender(g)}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border transition capitalize
                  ${bot.gender === g ? 'bg-violet-600 text-white shadow-[0_2px_10px_rgba(139,92,246,0.4)]' : 'bg-arena-100/60 dark:bg-arena-800 text-arena-700 dark:text-neon-200/75 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 hover:ring-arena-300/70 dark:hover:ring-arena-500/50'}`}>
                {g}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase text-arena-600 dark:text-neon-200/60 font-bold mb-1">Personality</label>
          <div className="flex gap-2">
            {(['easy', 'medium', 'hard'] as const).map((p) => (
              <button key={p} onClick={() => togglePersonality(p)}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border transition capitalize
                  ${bot.personality_type === p ? 'bg-violet-600 text-white shadow-[0_2px_10px_rgba(139,92,246,0.4)]' : 'bg-arena-100/60 dark:bg-arena-800 text-arena-700 dark:text-neon-200/75 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 hover:ring-arena-300/70 dark:hover:ring-arena-500/50'}`}>
                {p}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-arena-600 dark:text-neon-200/60 mt-1">
            {bot.personality_type === 'hard' ? "This bot will be picked often for hard-mode matches." :
             bot.personality_type === 'medium' ? "This bot joins medium-difficulty matches most often." :
             "This bot joins easy matches. Good for new players."}
          </p>
        </div>
        <div>
          <label className="block text-[10px] uppercase text-arena-600 dark:text-neon-200/60 font-bold mb-1">Status</label>
          <button onClick={toggleActive}
            className={`w-full px-3 py-2 rounded-lg text-xs font-semibold transition
              ${bot.is_active ? 'bg-emerald-600/20 text-emerald-700 dark:text-emerald-300 ring-1 ring-inset ring-emerald-600/40 hover:bg-emerald-600/30'
                              : 'bg-arena-100/60 dark:bg-arena-800 text-arena-400 dark:text-neon-200/70 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 hover:ring-arena-300/70 dark:hover:ring-arena-500/50'}`}>
            {bot.is_active ? 'ACTIVE — tap to deactivate' : 'INACTIVE — tap to activate'}
          </button>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t ring-arena-200/60 dark:ring-arena-600/40">
        <div className="text-[10px] uppercase text-red-400 font-bold mb-1">Danger zone</div>
        <p className="text-xs text-arena-400 dark:text-neon-200/70 mb-2">
          Type <b className="text-red-700 dark:text-red-300 font-mono">{bot.name}</b> to delete this bot. Ledger rows will be removed. This cannot be undone.
        </p>
        <div className="flex items-center gap-2">
          <input value={confirmDel} onChange={(e) => setConfirmDel(e.target.value)} placeholder={bot.name}
            className="flex-1 rounded-lg bg-arena-100/60 dark:bg-arena-800 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500" />
          <button onClick={doDelete} disabled={confirmDel !== bot.name}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed transition">
            <Icon name="delete_forever" size={14} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function WalletCard({ bot, reload, note }: { bot: Bot; reload: () => void; note: (s: string) => void }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<'credit' | 'debit'>('credit');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const amt = Math.round(Number(amount));
    if (!Number.isFinite(amt) || amt <= 0) { setErr('Enter a positive amount'); return; }
    setBusy(true);
    try {
      await adminApi.post(`/admin/bots/${bot.id}/wallet`, {
        delta: mode === 'credit' ? amt : -amt,
        reason: reason.trim() || undefined,
      });
      note(`${mode === 'credit' ? 'Credited' : 'Debited'}${amt}`);
      setAmount(''); setReason('');
      reload();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="rounded-2xl bg-gradient-to-br from-violet-900/30 to-purple-900/30 ring-1 ring-inset ring-violet-700/30 p-5">
      <div className="text-[10px] uppercase text-violet-700 dark:text-violet-300 font-bold mb-1">Current balance</div>
      <div className="text-3xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">{bot.wallet_balance.toLocaleString('en-IN')}</div>

      <div className="inline-flex rounded-lg bg-arena-100/60 dark:bg-arena-800 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 p-0.5 mb-3">
        <button type="button" onClick={() => setMode('credit')}
          className={`px-3 py-1.5 rounded text-xs font-bold ${mode === 'credit' ? 'bg-emerald-600 text-white' : 'text-arena-400'}`}>+ Credit</button>
        <button type="button" onClick={() => setMode('debit')}
          className={`px-3 py-1.5 rounded text-xs font-bold ${mode === 'debit' ? 'bg-red-600 text-white' : 'text-arena-400'}`}>− Debit</button>
      </div>
      <div className="space-y-2">
        <input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount"
          className="w-full rounded-lg bg-arena-100/60 dark:bg-arena-800 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-400" required />
        <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)"
          className="w-full rounded-lg bg-arena-100/60 dark:bg-arena-800 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400" />
      </div>
      {err && <div className="mt-2 text-xs text-red-400">{err}</div>}
      <button type="submit" disabled={busy}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white font-semibold py-2 text-sm disabled:opacity-50 transition">
        {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="save" size={14} />}
        {busy ? 'Applying…' : `Apply ${mode === 'credit' ? 'credit' : 'debit'}`}
      </button>
    </form>
  );
}

function StatsCard({ bot }: { bot: Bot }) {
  const winRate = bot.games_played > 0 ? Math.round((bot.matches_won / bot.games_played) * 100) : 0;

  // ── Bot-side P&L (the wallet view) ──────────────────────────────
  // botNet = +ve means the bot's wallet grew (bot was profitable from
  // its own perspective). −ve means the bot's wallet shrank.
  const botNet = bot.total_won - bot.total_spent;

  // ── Platform-side P&L (the admin view) ──────────────────────────
  // 2026-05-17 (Alternative Fee Policy):
  //   platform_match_yield   = total_spent − total_won  (sign-flipped)
  //   platform_fees_collected= sum(platform_fee_bot_share for this bot)
  //   platform_true_net_yield= match yield + fees
  // These are now returned by the GET /admin/bots/:id endpoint.
  // Fall back to client-side math if the backend hasn't been updated yet.
  const fees       = bot.platform_fees_collected   ?? 0;
  const feeCount   = bot.platform_fee_entries      ?? 0;
  const matchYield = bot.platform_match_yield      ?? (bot.total_spent - bot.total_won);
  const trueNet    = bot.platform_true_net_yield   ?? (matchYield + fees);

  const row = (label: string, value: string | number, color = 'text-arena-800 dark:text-neon-200/90') => (
    <div className="flex justify-between"><dt className="text-arena-600 dark:text-neon-200/60">{label}</dt><dd className={`font-semibold ${color}`}>{value}</dd></div>
  );
  const inr = (n: number) => n.toLocaleString('en-IN');
  return (
    <div className="rounded-2xl bg-white dark:bg-arena-800/60 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-4 space-y-4">
      {/* ── Activity counters ─────────────────────────────────── */}
      <div>
        <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2 mb-3">
          <Icon name="query_stats" size={18} className="text-arena-400 dark:text-neon-200/70" /> Lifetime stats
        </h3>
        <dl className="text-xs space-y-2">
          {row('Matches played', bot.games_played)}
          {row('Matches won',    bot.matches_won, 'text-emerald-400')}
          {row('Win rate',       `${winRate}%`)}
        </dl>
      </div>

      {/* ── Bot wallet P&L (raw wallet movements) ──────────────── */}
      <div>
        <h4 className="text-[11px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/60 mb-2 inline-flex items-center gap-1.5">
          <Icon name="account_balance_wallet" size={14} className="text-arena-400 dark:text-neon-200/70" />
          Bot wallet P&L
        </h4>
        <dl className="text-xs space-y-2">
          {row('Total spent (bets)', inr(bot.total_spent), 'text-red-400')}
          {row('Total won (prizes)', inr(bot.total_won),   'text-emerald-400')}
          {row('Bot net (wallet)',   inr(botNet), botNet >= 0 ? 'text-emerald-400' : 'text-red-400')}
        </dl>
      </div>

      {/* ── Platform P&L from this bot (admin's view) ────────── */}
      <div>
        <h4 className="text-[11px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/60 mb-2 inline-flex items-center gap-1.5">
          <Icon name="payments" size={14} className="text-violet-500" />
          Platform yield from this bot
        </h4>
        <dl className="text-xs space-y-2">
          {row('Match yield', `${matchYield >= 0 ? '+' : ''}${inr(matchYield)}`,
               matchYield > 0 ? 'text-emerald-400' : matchYield < 0 ? 'text-red-400' : 'text-arena-500')}
          {row(
            `Platform fees collected${feeCount > 0 ? ` (${feeCount})` : ''}`,
            `${fees >= 0 ? '+' : ''}${inr(fees)}`,
            fees > 0 ? 'text-emerald-400' : 'text-arena-500'
          )}
          {/* True net yield headline — bigger row, separator on top. */}
          <div className="pt-2 mt-1 border-t border-arena-200/60 dark:border-arena-600/40 flex justify-between items-baseline">
            <dt className="text-arena-700 dark:text-neon-200/80 font-semibold text-[12.5px] inline-flex items-center gap-1.5">
              <Icon name="auto_awesome" size={13} className="text-violet-500" />
              True net yield
            </dt>
            <dd className={`font-extrabold tabular-nums text-[14px] ${trueNet > 0 ? 'text-emerald-400' : trueNet < 0 ? 'text-red-400' : 'text-arena-500'}`}>
              {trueNet >= 0 ? '+' : ''}{inr(trueNet)}
            </dd>
          </div>
        </dl>
        <p className="text-[10px] text-arena-500 dark:text-neon-200/45 italic leading-relaxed mt-2">
          Match yield = bets minus prizes (sign-flipped to platform view).
          Fees collected = sum of <code>platform_fee_bot_share</code> on this bot's matches.
          True net yield = the two combined.
        </p>
      </div>
    </div>
  );
}

function LedgerCard({ botId }: { botId: number }) {
  const [data, setData] = useState<{ rows: LedgerRow[]; total: number } | null>(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const PAGE = 12;

  useEffect(() => {
    setLoading(true);
    adminApi.get<{ rows: LedgerRow[]; total: number }>(`/admin/bots/${botId}/ledger?limit=${PAGE}&offset=${page * PAGE}`)
      .then(setData).finally(() => setLoading(false));
  }, [botId, page]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;

  return (
    <div className="rounded-2xl bg-white dark:bg-arena-800/60 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-4 lg:col-span-1">
      <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2 mb-3">
        <Icon name="receipt_long" size={18} className="text-arena-400 dark:text-neon-200/70" /> Wallet ledger
        {data && <span className="ml-auto text-xs text-arena-600 dark:text-neon-200/60">{data.total} entries</span>}
      </h3>
      {loading && <div className="text-xs text-arena-600 dark:text-neon-200/60 text-center py-4">Loading…</div>}
      {!loading && data && (
        <>
          <ul className="space-y-1.5 max-h-[520px] overflow-y-auto pr-1">
            {data.rows.length === 0 && <li className="text-xs text-arena-600 dark:text-neon-200/60 text-center py-4">No wallet activity yet.</li>}
            {data.rows.map((r) => {
              const kind = REASON_LABEL[r.reason] || { label: r.reason, icon: 'circle' };
              const positive = r.direction === 'credit';
              return (
                <li key={r.id} className="flex items-center gap-2 rounded-lg bg-arena-100/60 dark:bg-arena-800/40 px-3 py-2">
                  <Icon name={kind.icon} size={16} className={positive ? 'text-emerald-400' : 'text-red-400'} />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-arena-800 dark:text-neon-200/90">
                      {kind.label} <span className="text-[10px] text-arena-600 dark:text-neon-200/60">· {gameLabel(r.game)}</span>
                    </div>
                    {r.note && <div className="text-[11px] text-arena-600 dark:text-neon-200/60 truncate">{r.note}</div>}
                    <div className="text-[10px] text-arena-700 dark:text-neon-200/80">
                      {new Date(r.created_at).toLocaleString()}
                      {r.match_ref && <span> · match #{r.match_ref}</span>}
                      {r.actor_admin_id && <span> · admin #{r.actor_admin_id}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-mono font-semibold ${positive ? 'text-emerald-400' : 'text-red-400'}`}>
                      {positive ? '+' : '−'}{r.amount}
                    </div>
                    <div className="text-[10px] text-arena-600 dark:text-neon-200/60 font-mono">bal{r.balance_after}</div>
                  </div>
                </li>
              );
            })}
          </ul>
          {data.total > PAGE && (
            <div className="mt-3 flex items-center justify-between text-xs">
              <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-arena-100/60 dark:bg-arena-800 hover:bg-arena-100/60 dark:bg-arena-700/60 disabled:opacity-40">
                <Icon name="chevron_left" size={14} /> Prev
              </button>
              <span className="text-arena-600 dark:text-neon-200/60">{page + 1} / {totalPages}</span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}
                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-arena-100/60 dark:bg-arena-800 hover:bg-arena-100/60 dark:bg-arena-700/60 disabled:opacity-40">
                Next <Icon name="chevron_right" size={14} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
